// window.Pulse —— 廣告 SDK 風格的命令式 API。讓宿主頁面在 runtime 以動態參數
// （brand / category / mpid）投放問卷 slot，補足 mount.ts 的 declarative data-* 自動掛載，
// 並提供 open()/close() 讓整合方以自家按鈕 / 捲動邏輯手動觸發彈出。
// 只有 IIFE 進入點 (embed.ts) 會 installSdk()；package 進入點 ./index 維持不汙染全域
// （admin 預覽 import ./index 不會掛上 window.Pulse）。
import type { PulseSurvey } from './survey-element'
import { autoMount } from './mount'

interface RenderOptions {
  // 目標容器：CSS selector 或 Element。
  el: string | Element
  brandId: string
  // 選填且可為 null：文章分類，命中就作為 GET 的 category query。整合方嵌在沒有分類的頁面（如首頁）
  // 時傳 null 或直接省略，代表「此版位無分類、不投放」，widget 不設 category-id、runtime 收合成
  // no-config 且不打 API（見 survey-element.ts load()）。
  categoryId?: string | null
  mpid?: string
  layout?: 'inline' | 'sidebar' | 'modal' | 'manual'
  lazy?: boolean
}

type CmdQueue = Array<() => void> | { push: (fn: () => void) => void }

interface PulseGlobal {
  render?: (opts: RenderOptions) => HTMLElement | null
  // 手動觸發：target = selector 或 Element（元件本身或其容器）。回傳是否命中一個 <pulse-survey>。
  open?: (target: string | Element) => boolean
  close?: (target: string | Element) => boolean
  // 重新掃描宣告式嵌入（[data-survey-zone] / script[data-brand-id]），供 SPA 動態插入版位後
  // 主動觸發重掛。idempotent（沿用 mount.ts 的 WeakSet 去重），非長壽命 observer——host 自行決定
  // 何時呼叫（例如路由變化後）。root 省略時掃整個 document；接受 null（如 querySelector 未命中的
  // 回傳）並直接忽略，讓 host 免去呼叫前的 null 檢查。
  refresh?: (root?: ParentNode | null) => void
  cmd?: CmdQueue
  // Optional runtime override of the API base URL (defaults to the build-time
  // VITE_API_BASE_URL). Read per request, so it can also be set via the cmd queue;
  // production only honours https overrides. Used by the showcase demo to target the mock API.
  apiBase?: string
  // Optional runtime override of the static-asset base URL used to build IP logo URLs
  // (defaults to the build-time VITE_ASSET_BASE_URL; see design-system/logos.ts). Read per
  // render of the `<pulse-survey>` header — set this before the element renders (before it
  // connects, or before a later re-render) for it to take effect; changing it afterwards only
  // applies starting from the next render. production only honours https hosts on the asset
  // allowlist (src/asset-origin.ts); anything else is ignored and warned once. Lets a host point
  // the widget's logo at a staging CDN before that environment is promoted to production.
  assetBase?: string
}

declare global {
  interface Window {
    Pulse?: PulseGlobal
  }
}

function render(opts: RenderOptions): HTMLElement | null {
  const target = typeof opts.el === 'string' ? document.querySelector(opts.el) : opts.el
  if (!target)
    return null
  const el = document.createElement('pulse-survey')
  el.setAttribute('brand-id', opts.brandId)
  if (opts.categoryId)
    el.setAttribute('category-id', opts.categoryId)
  if (opts.mpid)
    el.setAttribute('mpid', opts.mpid)
  const layout = opts.layout ?? 'inline'
  el.setAttribute('layout', layout)
  // inline / sidebar 版位預設 lazy（接近 viewport 才 fetch），避免 fold 下的版位搶首屏網路；
  // manual 是 headless 觸發、維持 eager 讓內容早就緒。整合方可用 lazy: false 明確關閉。
  if (opts.lazy ?? (layout === 'inline' || layout === 'sidebar'))
    el.setAttribute('lazy', '')
  target.appendChild(el)
  return el
}

// 解析 target 至元件本身，或其容器內第一個 <pulse-survey>。
function resolveSurvey(target: string | Element): PulseSurvey | null {
  const node = typeof target === 'string' ? document.querySelector(target) : target
  if (!node)
    return null
  if (node instanceof HTMLElement && node.tagName.toLowerCase() === 'pulse-survey')
    return node as PulseSurvey
  return node.querySelector('pulse-survey')
}

function open(target: string | Element): boolean {
  const el = resolveSurvey(target)
  el?.open()
  return el !== null
}

function close(target: string | Element): boolean {
  const el = resolveSurvey(target)
  el?.close()
  return el !== null
}

// Re-run the declarative auto-mount scan on demand. Delegates straight to mount.ts's autoMount()
// — same module-level WeakSet dedup — so a host can call this after inserting a new zone (SPA
// route change) without the SDK running a long-lived MutationObserver.
// 接受 null（如 querySelector 未命中的回傳）並忽略：JS 預設參數只在 undefined 生效，autoMount(null)
// 會 deref null 崩潰——加防護讓 host 可直接把 DOM 查詢結果丟進來，不必自己先做 null 檢查。
function refresh(root?: ParentNode | null): void {
  if (root === null)
    return
  autoMount(root)
}

// googletag 式：頁面可在本 IIFE（async）載入前就 `window.Pulse.cmd.push(fn)`。
// install 時把已排隊的命令 drain 掉，再把 cmd.push 換成立即執行。
export function installSdk(): void {
  const existing = window.Pulse
  const pending = existing?.cmd
  const queued = Array.isArray(pending) ? pending : []
  window.Pulse = {
    render,
    open,
    close,
    refresh,
    cmd: { push: (fn: () => void) => { fn() } },
    // Preserve a host-provided apiBase override — installSdk rebuilds window.Pulse
    // but must not clobber it (PulseGlobal documents apiBase as a host-settable,
    // read-per-request override; resolveBaseUrl reads it live on each call).
    ...(existing?.apiBase !== undefined ? { apiBase: existing.apiBase } : {}),
    // Same reasoning for assetBase — a host may set window.Pulse.assetBase (directly or via the
    // cmd queue) before this IIFE finishes installing; installSdk must not clobber it.
    ...(existing?.assetBase !== undefined ? { assetBase: existing.assetBase } : {}),
  }
  for (const fn of queued) {
    try {
      fn()
    }
    catch (err) {
      console.error('[Pulse] queued command failed', err)
    }
  }
}
